import { DatasetName } from "./constants.ts";
import type { FieldType } from "./report-configuration.ts";

export interface DateFieldBase {
  datasetName: DatasetName[];
  field: string;
  group: string;
  label: string;
  type: Extract<FieldType, "date">;
}

export interface NonDateFieldBase {
  datasetName: DatasetName[];
  field: string;
  group: string;
  label: string;
  type: Exclude<FieldType, "date">;
}

export type FieldBase = DateFieldBase | NonDateFieldBase;

export type FieldName =
  | "ACTION_TAKEN"
  | "ACTIVITY_DATE"
  | "AGE_GROUP"
  | "ANNUALIZED_TOTAL_CASH_COMPENSATION"
  | "ANSWER_VALUE"
  | "APPLICANT_COUNT"
  | "APPLICANT_SOURCE"
  | "APPLICANT_SOURCE_DETAIL"
  | "APPLICATION_DATE"
  | "APPLICATION_ID"
  | "ATTRITION_DRIVERS_IMPORTANCE"
  | "ATTRITION_PROBABILITY"
  | "ATTRITION_PROBABILITY_TIME_HORIZON"
  | "BONUS_AMOUNT"
  | "BONUS_REASON"
  | "COMMISSION_AMOUNT"
  | "COMMISSION_REASON"
  | "COMPANY_ID"
  | "COMPANY_SIZE"
  | "COMPLETION_CATEGORY"
  | "COMPLETION_VELOCITY_DAYS"
  | "COUNTRY"
  | "DATE"
  | "DATE_OF_PROMOTION"
  | "DEPARTMENT"
  | "DISCIPLINARY_STATUS"
  | "DISCUSSION_DATE"
  | "DIVISION"
  | "DISABILITY_STATUS"
  | "EDUCATION"
  | "EMPLOYEE_COUNT"
  | "EMPLOYEE_COUNTY_NAME"
  | "EMPLOYEE_GOAL_CREATED_DATE"
  | "EMPLOYEE_GOAL_COMPLETED_DATE"
  | "EMPLOYEE_GOAL_COMPLETION_PERCENT"
  | "EMPLOYEE_GOAL_DUE_DATE"
  | "EMPLOYEE_GOAL_TYPE"
  | "EMPLOYEE_GOAL_STATUS"
  | "EMPLOYEE_MSA_NAME"
  | "EMPLOYMENT_TYPE"
  | "ETHNICITY"
  | "EXPECTED_START_DATE"
  | "GENDER"
  | "GENERATION"
  | "HIRE_DATE"
  | "HIRED_DATE"
  | "HIRING_MANAGER"
  | "INDUSTRY"
  | "INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY"
  | "INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION"
  | "INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS"
  | "INDUSTRY_SEPARATION_INTENSITY"
  | "INDUSTRY_TOTAL_POSITIONS"
  | "INFRACTION_DESCRIPTION"
  | "INSIGHTS_8_TYPE"
  | "INSIGHTS_OPPOSITE_8_TYPE"
  | "INSIGHTS_16_TYPE"
  | "INSIGHTS_CONSCIOUS_BLUE_ENERGY"
  | "INSIGHTS_CONSCIOUS_RED_ENERGY"
  | "INSIGHTS_CONSCIOUS_GREEN_ENERGY"
  | "INSIGHTS_CONSCIOUS_YELLOW_ENERGY"
  | "INSIGHTS_LEADING_CONSCIOUS_COLOR"
  | "INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_RED_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY"
  | "INSIGHTS_SUPERVISOR_SAME_8_TYPE"
  | "INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE"
  | "JOB_BAND"
  | "JOB_CATEGORY"
  | "JOB_FAMILY"
  | "JOB_HIERARCHY"
  | "JOB_TITLE"
  | "JOB_REQ_ACCEPTANCE_RATE"
  | "JOB_REQ_FILL_DATE"
  | "JOB_REQ_ID"
  | "JOB_REQ_OPEN_DATE"
  | "JOB_REQ_STATUS"
  | "LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_EMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE"
  | "LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY"
  | "LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_UNEMPLOYED_WORKERS"
  | "LAG_TIME_DAYS"
  | "LAG_TIME_BUCKET"
  | "LGBTQ_STATUS"
  | "LOCATION"
  | "MARKET_PRESSURE_ROLE"
  | "MARKET_PRESSURE_MARKET"
  | "NAICS_NAME"
  | "OCCUPATION_MARKET_EMP_CHANGE_YOY"
  | "OCCUPATION_MARKET_GROWTH_OPENINGS"
  | "OCCUPATION_MARKET_HIRES"
  | "OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM"
  | "OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE"
  | "OCCUPATION_MARKET_REPLACEMENT_OPENINGS"
  | "OCCUPATION_MARKET_RESIDENT_EMPLOYMENT"
  | "OCCUPATION_MARKET_SEPARATIONS"
  | "OCCUPATION_MARKET_YOY_HIRES_CHANGE"
  | "OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE"
  | "OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE"
  | "OCCUPATION_LOCAL_HIRE_RATE"
  | "OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE"
  | "OCCUPATION_OCCUPIED_POSITIONS"
  | "OCCUPATION_NATIONAL_HIRE_RATE"
  | "OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE"
  | "OCCUPATION_SEPARATION_INTENSITY"
  | "ORIGIN"
  | "OSHA_INCIDENT_DATE"
  | "OSHA_INCIDENT_TYPE"
  | "OSHA_AFFECTED_BODY_PART"
  | "OSHA_FIRST_AID_ONLY"
  | "OSHA_ON_COMPANY_PROPERTY"
  | "OSHA_OCCUPATION"
  | "OSHA_ESTABLISHMENT_NAME"
  | "PAY_BAND"
  | "PERFORMANCE_RATING"
  | "POSITIONS_IN_INDUSTRY_AND_OCCUPATION"
  | "PRIMARY_RECRUITER"
  | "PRIMARY_WORK_LOCATION"
  | "QUESTION_SUBTHEME"
  | "QUESTION_THEME"
  | "QUESTION_LABEL"
  | "RACE"
  | "RACE_GROUPING"
  | "RECRUITMENT_STAGE"
  | "RECRUITMENT_STAGE_HISTORICAL"
  | "REGION"
  | "REGRETTABLE_ATTRITION"
  | "SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT"
  | "SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE"
  | "SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY"
  | "SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS"
  | "SOC_TITLE"
  | "STATE"
  | "STORE_NAME"
  | "STORE_STATUS"
  | "SUBMIT_DATE"
  | "SUPERVISOR_ID"
  | "SUPERVISOR_JOB_TITLE"
  | "SUPERVISOR_NAME"
  | "SURVEY_NAME"
  | "SURVEY_STATUS"
  | "TENURE_GROUP"
  | "TERMINATION_DATE"
  | "TERMINATION_REASON"
  | "TERMINATION_TYPE"
  | "TIME_TO_FILL_DAYS"
  | "TIME_TO_FILL_DRIVERS_IMPACT"
  | "TIME_TO_HIRE_DAYS"
  | "TOTAL_COMPENSATION"
  | "VETERAN_STATUS"
  | "WORK_ARRANGEMENT_TYPE";

export const FIELD_BASES = {
  ACTION_TAKEN: {
    datasetName: ["employee_relations"],
    label: "Action Type",
    field: "Action Taken",
    group: "Employee Relations",
    type: "string",
  },
  ACTIVITY_DATE: {
    datasetName: ["employee_relations"],
    label: "Activity Date",
    field: "Activity Date",
    group: "Employee Relations",
    type: "date",
  },
  AGE_GROUP: {
    datasetName: ["main", "culture"],
    field: "age_group",
    label: "Age Group",
    group: "Demographics",
    type: "string",
  },
  ANNUALIZED_TOTAL_CASH_COMPENSATION: {
    datasetName: ["main", "employee_goals"],
    label: "Total Cash Compensation",
    field: "annualized_total_cash_compensation",
    group: "Organization",
    type: "number",
  },
  ANSWER_VALUE: {
    datasetName: ["culture"],
    label: "Survey Answers",
    field: "answer_value",
    group: "Organization",
    type: "number",
  },
  APPLICANT_COUNT: {
    datasetName: ["recruitment_applications"],
    label: "Applicant Count",
    field: "Applicant Count",
    group: "Recruitment",
    type: "number",
  },
  APPLICANT_SOURCE: {
    datasetName: ["main", "recruitment_applications"],
    label: "Source",
    field: "Source",
    group: "Recruitment",
    type: "string",
  },
  APPLICANT_SOURCE_DETAIL: {
    datasetName: ["main", "recruitment_applications"],
    label: "Source Detail",
    field: "Source Detail",
    group: "Recruitment",
    type: "string",
  },
  APPLICATION_DATE: {
    datasetName: ["recruitment_applications"],
    label: "Application Date",
    field: "Applied_Date",
    group: "Recruitment",
    type: "date",
  },
  APPLICATION_ID: {
    datasetName: ["recruitment_applications"],
    label: "Application ID",
    field: "Application ID",
    group: "Recruitment",
    type: "string",
  },
  ATTRITION_DRIVERS_IMPORTANCE: {
    datasetName: ["attrition_drivers"],
    label: "Attrition Drivers",
    field: "Importance",
    group: "Organization",
    type: "number",
  },
  ATTRITION_PROBABILITY: {
    datasetName: ["main", "culture"],
    label: "Attrition Probability",
    field: "attrition_6m",
    group: "Organization",
    type: "number",
  },
  ATTRITION_PROBABILITY_TIME_HORIZON: {
    datasetName: ["main"],
    label: "Attrition Time Horizon",
    field: "attrition_6m",
    group: "Organization",
    type: "select",
  },
  BONUS_AMOUNT: {
    datasetName: ["main"],
    label: "Bonus Amount",
    field: "bonusAmount",
    group: "Organization",
    type: "number",
  },
  BONUS_REASON: {
    datasetName: ["main"],
    label: "Bonus Reason",
    field: "bonusReason",
    group: "Organization",
    type: "string",
  },
  COMMISSION_AMOUNT: {
    datasetName: ["main"],
    label: "Commission Amount",
    field: "commissionAmount",
    group: "Organization",
    type: "number",
  },
  COMMISSION_REASON: {
    datasetName: ["main"],
    label: "Commission Reason",
    field: "commissionReason",
    group: "Organization",
    type: "string",
  },
  COMPANY_SIZE: {
    datasetName: ["benchmark"],
    label: "Company Size",
    field: "CompanySize",
    group: "Benchmark",
    type: "string",
  },
  COMPANY_ID: {
    datasetName: ["main", "lightcast"],
    label: "Company ID",
    field: "companyId",
    group: "Organization",
    type: "string",
  },
  COMPLETION_CATEGORY: {
    datasetName: ["employee_goals"],
    label: "Completion Category",
    field: "completionCategory",
    group: "Employee Goals",
    type: "string",
  },
  COMPLETION_VELOCITY_DAYS: {
    datasetName: ["employee_goals"],
    label: "Completion Velocity (Days)",
    field: "completionVelocity_days",
    group: "Employee Goals",
    type: "number",
  },
  COUNTRY: {
    datasetName: ["main", "benchmark", "employee_goals"],
    label: "Country",
    field: "country",
    group: "Geography",
    type: "string",
  },
  DATE: {
    datasetName: ["main"],
    label: "Date",
    field: "Date",
    group: "Time",
    type: "date",
  },
  DATE_OF_PROMOTION: {
    datasetName: ["main", "culture"],
    label: "Date of Promotion",
    field: "Date of Promotion",
    group: "Time",
    type: "date",
  },
  DEPARTMENT: {
    datasetName: [
      "main",
      "recruitment_applications",
      "culture",
      "employee_goals",
    ],
    field: "department",
    label: "Department",
    group: "Organization",
    type: "string",
  },
  DISCIPLINARY_STATUS: {
    datasetName: ["employee_relations"],
    label: "Disciplinary Status",
    field: "Disciplinary Status",
    group: "Employee Relations",
    type: "string",
  },
  DISCUSSION_DATE: {
    datasetName: ["employee_relations"],
    label: "Discussion Date",
    field: "Discussion Date",
    group: "Employee Relations",
    type: "date",
  },
  DISABILITY_STATUS: {
    datasetName: ["main", "recruitment_applications"],
    label: "Disability Status",
    field: "disability_status",
    group: "Demographics",
    type: "string",
  },
  DIVISION: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Division",
    field: "division",
    group: "Organization",
    type: "string",
  },
  EDUCATION: {
    datasetName: ["main"],
    label: "Education",
    field: "education",
    group: "Demographics",
    type: "string",
  },
  EMPLOYEE_COUNT: {
    datasetName: ["benchmark"],
    label: "Employee Count",
    field: "EmployeeCount",
    group: "Benchmark",
    type: "number",
  },
  EMPLOYEE_COUNTY_NAME: {
    datasetName: ["main", "lightcast"],
    label: "Employee County Name",
    field: "county_name",
    group: "Industry",
    type: "string",
  },
  EMPLOYEE_GOAL_CREATED_DATE: {
    datasetName: ["employee_goals"],
    label: "Goal Created Date",
    field: "employeeGoalCreatedDateTime",
    group: "Employee Goals",
    type: "date",
  },
  EMPLOYEE_GOAL_COMPLETED_DATE: {
    datasetName: ["employee_goals"],
    label: "Goal Completed Date",
    field: "employeeGoalCompletedDateTime",
    group: "Employee Goals",
    type: "date",
  },
  EMPLOYEE_GOAL_COMPLETION_PERCENT: {
    datasetName: ["employee_goals"],
    label: "Goal Completion Percent",
    field: "employeeGoalCompletionPercent",
    group: "Employee Goals",
    type: "number",
  },
  EMPLOYEE_GOAL_DUE_DATE: {
    datasetName: ["employee_goals"],
    label: "Goal Due Date",
    field: "employeeGoalDueDate",
    group: "Employee Goals",
    type: "date",
  },
  EMPLOYEE_GOAL_TYPE: {
    datasetName: ["employee_goals"],
    label: "Goal Type",
    field: "employeeGoalType",
    group: "Employee Goals",
    type: "string",
  },
  EMPLOYEE_GOAL_STATUS: {
    datasetName: ["employee_goals"],
    label: "Goal Status",
    field: "employeeGoalStatus",
    group: "Employee Goals",
    type: "string",
  },
  EMPLOYEE_MSA_NAME: {
    datasetName: ["main", "lightcast"],
    label: "MSA Name",
    field: "msa_name",
    group: "Industry",
    type: "string",
  },
  EMPLOYMENT_TYPE: {
    datasetName: ["main"],
    label: "Employment Type",
    field: "employmentType",
    group: "Organization",
    type: "string",
  },
  ETHNICITY: {
    datasetName: ["main", "recruitment_applications"],
    label: "Ethnicity",
    field: "ethnicity",
    group: "Demographics",
    type: "string",
  },
  EXPECTED_START_DATE: {
    datasetName: ["recruitment_applications"],
    label: "Expected Start Date",
    field: "Expected Start Date",
    group: "Recruitment",
    type: "date",
  },
  GENDER: {
    datasetName: [
      "main",
      "benchmark",
      "recruitment_applications",
      "culture",
      "employee_goals",
    ],
    label: "Gender",
    field: "Gender",
    group: "Demographics",
    type: "string",
  },
  GENERATION: {
    datasetName: ["main"],
    label: "Generation",
    field: "Generation",
    group: "Demographics",
    type: "string",
  },
  HIRE_DATE: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Hire Date",
    field: "hireDate",
    group: "Organization",
    type: "date",
  },
  HIRED_DATE: {
    datasetName: ["recruitment_applications"],
    label: "Hired Date",
    field: "Hired_Date",
    group: "Recruitment",
    type: "date",
  },
  HIRING_MANAGER: {
    datasetName: ["main", "recruitment_applications"],
    label: "Hiring Manager",
    field: "Hiring Manager",
    group: "Recruitment",
    type: "string",
  },
  INDUSTRY: {
    datasetName: ["benchmark"],
    label: "Industry",
    field: "Industry",
    group: "Benchmark",
    type: "string",
  },
  INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY: {
    datasetName: ["lightcast"],
    label: "Industry Jobs Change (YoY)",
    field: "industry_context_industry_positions_change_yoy",
    group: "Industry",
    type: "number",
  },
  INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION: {
    datasetName: ["lightcast"],
    label: "Industry Share of Occupation",
    field: "industry_context_industry_share_of_occupation",
    group: "Industry",
    type: "number",
  },
  INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS: {
    datasetName: ["lightcast"],
    label: "Jobs in Occupation (All Industries)",
    field: "industry_context_occupation_total_positions",
    group: "Industry",
    type: "number",
  },
  INDUSTRY_SEPARATION_INTENSITY: {
    datasetName: ["main", "lightcast"],
    label: "Industry Exit Rate (Market)",
    field: "industry_context_blended_industry_separation_intensity",
    group: "Industry",
    type: "number",
  },
  INDUSTRY_TOTAL_POSITIONS: {
    datasetName: ["main", "lightcast"],
    label: "Jobs in Industry (Market)",
    field: "industry_context_industry_total_positions",
    group: "Industry",
    type: "number",
  },
  INFRACTION_DESCRIPTION: {
    datasetName: ["employee_relations"],
    label: "Infraction Description",
    field: "Description",
    group: "Employee Relations",
    type: "string",
  },
  INSIGHTS_8_TYPE: {
    datasetName: ["main", "culture"],
    label: "8 Type",
    field: "eightTypeText",
    group: "Insights Discovery",
    type: "string",
  },
  INSIGHTS_OPPOSITE_8_TYPE: {
    datasetName: ["main", "culture"],
    label: "Opposite 8 Type ",
    field: "oppositeEightTypeText",
    group: "Insights Discovery",
    type: "string",
  },
  INSIGHTS_16_TYPE: {
    datasetName: ["main", "culture"],
    label: "16 Type",
    field: "sixteenTypeText",
    group: "Insights Discovery",
    type: "string",
  },
  INSIGHTS_CONSCIOUS_BLUE_ENERGY: {
    datasetName: ["main", "culture"],
    label: "Blue",
    field: "conscious_coolBlueEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_CONSCIOUS_GREEN_ENERGY: {
    datasetName: ["main", "culture"],
    label: "Green",
    field: "conscious_earthGreenEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_CONSCIOUS_RED_ENERGY: {
    datasetName: ["main", "culture"],
    label: "Red",
    field: "conscious_fieryRedEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_CONSCIOUS_YELLOW_ENERGY: {
    datasetName: ["main", "culture"],
    label: "Yellow",
    field: "conscious_sunshineYellowEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_LEADING_CONSCIOUS_COLOR: {
    datasetName: ["main", "culture"],
    label: "Lead Color Energy",
    field: "colorEnergy",
    group: "Insights Discovery",
    type: "string",
  },
  INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY: {
    datasetName: ["main", "culture"],
    label: "BlueLC",
    field: "less_conscious_coolBlueEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY: {
    datasetName: ["main", "culture"],
    label: "GreenLC",
    field: "less_conscious_earthGreenEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_LESS_CONSCIOUS_RED_ENERGY: {
    datasetName: ["main", "culture"],
    label: "RedLC",
    field: "less_conscious_fieryRedEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY: {
    datasetName: ["main", "culture"],
    label: "YellowLC",
    field: "less_conscious_sunshineYellowEnergy",
    group: "Insights Discovery",
    type: "number",
  },
  INSIGHTS_SUPERVISOR_SAME_8_TYPE: {
    datasetName: ["main", "culture"],
    label: "Manager Same Type",
    field: "supervisor_same_type",
    group: "Insights Discovery",
    type: "string",
  },
  INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE: {
    datasetName: ["main", "culture"],
    label: "Manager Opposite Type",
    field: "supervisor_opposite_type",
    group: "Insights Discovery",
    type: "string",
  },
  JOB_BAND: {
    datasetName: ["main"],
    label: "Job Band",
    field: "jobBand",
    group: "Organization",
    type: "string",
  },
  JOB_CATEGORY: {
    datasetName: ["main"],
    label: "Job Category",
    field: "jobCategory",
    group: "Organization",
    type: "string",
  },
  JOB_FAMILY: {
    datasetName: ["main", "recruitment_applications"],
    label: "Job Family",
    field: "jobFamily",
    group: "Organization",
    type: "string",
  },
  JOB_HIERARCHY: {
    datasetName: ["main"],
    label: "Job Hierarchy",
    field: "jobHierarchy",
    group: "Organization",
    type: "string",
  },
  JOB_REQ_ACCEPTANCE_RATE: {
    datasetName: ["recruitment_applications"],
    label: "Job Req Acceptance Rate",
    field: "Acceptance Rate",
    group: "Recruitment",
    type: "number",
  },
  JOB_REQ_FILL_DATE: {
    datasetName: ["recruitment_applications"],
    label: "Fill Date",
    field: "Fill Date",
    group: "Recruitment",
    type: "date",
  },
  JOB_REQ_ID: {
    datasetName: ["main", "recruitment_applications"],
    label: "Job Req ID",
    field: "Requisition ID",
    group: "Recruitment",
    type: "string",
  },
  JOB_REQ_OPEN_DATE: {
    datasetName: ["recruitment_applications"],
    label: "Open Date",
    field: "Recruiting Start Date",
    group: "Recruitment",
    type: "date",
  },
  JOB_REQ_STATUS: {
    datasetName: ["recruitment_applications"],
    label: "Status",
    field: "Status",
    group: "Recruitment",
    type: "string",
  },
  JOB_TITLE: {
    datasetName: ["main", "recruitment_applications", "employee_goals"],
    label: "Job Title",
    field: "jobTitle",
    group: "Organization",
    type: "string",
  },
  LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO: {
    datasetName: ["lightcast"],
    label: "Current Labor Availability Ratio",
    field: "labor_availability_current_labor_availability_ratio",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_EMPLOYED_WORKERS: {
    datasetName: ["lightcast"],
    label: "Employed Workers",
    field: "labor_availability_employed_workers",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE: {
    datasetName: ["lightcast"],
    label: "Labor Availability vs. 12M Average",
    field: "labor_availability_labor_availability_change_from_12m_average",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY: {
    datasetName: ["lightcast"],
    label: "Local vs. National Labor Availability",
    field: "labor_availability_local_minus_national_labor_availability",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO: {
    datasetName: ["lightcast"],
    label: "National Labor Availability Ratio",
    field: "labor_availability_national_current_labor_availability_ratio",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS: {
    datasetName: ["lightcast"],
    label: "Employed Workers (Trailing 12M)",
    field: "labor_availability_trailing_12m_employed_workers",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO: {
    datasetName: ["lightcast"],
    label: "Labor Availability Ratio (Trailing 12M)",
    field: "labor_availability_trailing_12m_labor_availability_ratio",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS: {
    datasetName: ["lightcast"],
    label: "Unemployed Workers (Trailing 12M)",
    field: "labor_availability_trailing_12m_unemployed_workers",
    group: "Industry",
    type: "number",
  },
  LABOR_AVAILABILITY_UNEMPLOYED_WORKERS: {
    datasetName: ["lightcast"],
    label: "Unemployed Workers",
    field: "labor_availability_unemployed_workers",
    group: "Industry",
    type: "number",
  },
  LAG_TIME_DAYS: {
    datasetName: ["osha_incidents"],
    label: "Lag Time (Days)",
    field: "lagTime_days",
    group: "OSHA Incidents",
    type: "number",
  },
  LAG_TIME_BUCKET: {
    datasetName: ["osha_incidents"],
    label: "Lag Time Bucket",
    field: "lagTime_bucket",
    group: "OSHA Incidents",
    type: "string",
  },
  LGBTQ_STATUS: {
    datasetName: ["main"],
    label: "LGBTQ Status",
    field: "LGBTQ_status",
    group: "Demographics",
    type: "string",
  },
  LOCATION: {
    datasetName: ["main", "employee_relations", "employee_goals"],
    label: "Location",
    field: "Location",
    group: "Organization",
    type: "string",
  },
  MARKET_PRESSURE_ROLE: {
    datasetName: ["main"],
    label: "Market Pressure (Role)",
    field: "mpi_score_soc",
    group: "Industry",
    type: "number",
  },
  MARKET_PRESSURE_MARKET: {
    datasetName: ["main"],
    label: "Market Pressure (Market)",
    field: "mpi_score_geo",
    group: "Industry",
    type: "number",
  },
  NAICS_NAME: {
    datasetName: ["main", "lightcast"],
    label: "NAICS Name",
    field: "naics_name",
    group: "Industry",
    type: "string",
  },
  OCCUPATION_MARKET_EMP_CHANGE_YOY: {
    datasetName: ["lightcast"],
    label: "Occupation Jobs Change (YoY)",
    field: "occupation_market_emp_change_yoy",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_GROWTH_OPENINGS: {
    datasetName: ["lightcast"],
    label: "Growth Openings",
    field: "occupation_market_growth_openings",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_HIRES: {
    datasetName: ["lightcast"],
    label: "Occupation Hires",
    field: "occupation_market_hires",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM: {
    datasetName: ["lightcast"],
    label: "Local Earnings Premium",
    field: "occupation_market_local_earnings_premium",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE: {
    datasetName: ["lightcast"],
    label: "Replacement Opening Rate",
    field: "occupation_market_replacement_opening_rate",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_REPLACEMENT_OPENINGS: {
    datasetName: ["lightcast"],
    label: "Replacement Openings",
    field: "occupation_market_replacement_openings",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_RESIDENT_EMPLOYMENT: {
    datasetName: ["lightcast"],
    label: "Resident Employment",
    field: "occupation_market_resident_employment",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_SEPARATIONS: {
    datasetName: ["lightcast"],
    label: "Occupation Separations",
    field: "occupation_market_separations",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_YOY_HIRES_CHANGE: {
    datasetName: ["lightcast"],
    label: "Occupation Hires Change (YoY)",
    field: "occupation_market_yoy_hires_change",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE: {
    datasetName: ["lightcast"],
    label: "Replacement Openings Change (YoY)",
    field: "occupation_market_yoy_replacement_openings_change",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE: {
    datasetName: ["lightcast"],
    label: "Occupation Separations Change (YoY)",
    field: "occupation_market_yoy_separations_change",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_LOCAL_HIRE_RATE: {
    datasetName: ["main", "lightcast"],
    label: "Local Hire Rate",
    field: "occupation_market_local_hire_rate",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE: {
    datasetName: ["main", "lightcast"],
    label: "Local vs National Hiring",
    field: "occupation_market_local_minus_national_hire_rate",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_NATIONAL_HIRE_RATE: {
    datasetName: ["main", "lightcast"],
    label: "National Hire Rate",
    field: "occupation_market_national_hire_rate",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE: {
    datasetName: ["main", "lightcast"],
    label: "Role Growth Rate",
    field: "occupation_market_net_employment_growth_rate",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_OCCUPIED_POSITIONS: {
    datasetName: ["main", "lightcast"],
    label: "Jobs in Role (Market)",
    field: "occupation_market_occupied_positions",
    group: "Industry",
    type: "number",
  },
  OCCUPATION_SEPARATION_INTENSITY: {
    datasetName: ["main", "lightcast"],
    label: "Role Exit Rate (Market)",
    field: "occupation_market_separation_intensity",
    group: "Industry",
    type: "number",
  },
  ORIGIN: {
    datasetName: ["main", "recruitment_applications"],
    label: "Origin",
    field: "Origin",
    group: "Recruitment",
    type: "string",
  },
  OSHA_INCIDENT_DATE: {
    datasetName: ["osha_incidents"],
    label: "OSHA Incident Date",
    field: "OSHA Incident Date",
    group: "OSHA Incidents",
    type: "date",
  },
  OSHA_INCIDENT_TYPE: {
    datasetName: ["osha_incidents"],
    label: "OSHA Incident Type",
    field: "Incident Type",
    group: "OSHA Incidents",
    type: "string",
  },
  OSHA_AFFECTED_BODY_PART: {
    datasetName: ["osha_incidents"],
    label: "Affected Body Part",
    field: "Affected Body Part",
    group: "OSHA Incidents",
    type: "string",
  },
  OSHA_FIRST_AID_ONLY: {
    datasetName: ["osha_incidents"],
    label: "First Aid Only",
    field: "First Aid Only",
    group: "OSHA Incidents",
    type: "string",
  },
  OSHA_ON_COMPANY_PROPERTY: {
    datasetName: ["osha_incidents"],
    label: "On Company Property",
    field: "On Company Property",
    group: "OSHA Incidents",
    type: "string",
  },
  OSHA_OCCUPATION: {
    datasetName: ["osha_incidents"],
    label: "Occupation",
    field: "Occupation",
    group: "OSHA Incidents",
    type: "string",
  },
  OSHA_ESTABLISHMENT_NAME: {
    datasetName: ["osha_incidents"],
    label: "OSHA Establishment Name",
    field: "OSHA Establishment Name",
    group: "OSHA Incidents",
    type: "string",
  },
  PAY_BAND: {
    datasetName: ["main"],
    label: "Pay Band",
    field: "pay_band",
    group: "Organization",
    type: "string",
  },
  PERFORMANCE_RATING: {
    datasetName: ["main", "culture"],
    label: "Performance Rating",
    field: "Performance_Rating",
    group: "Organization",
    type: "number",
  },
  POSITIONS_IN_INDUSTRY_AND_OCCUPATION: {
    datasetName: ["main", "lightcast"],
    label: "Jobs in Industry and Role",
    field: "industry_context_positions_in_industry_occupation",
    group: "Industry",
    type: "number",
  },
  PRIMARY_RECRUITER: {
    datasetName: ["main", "recruitment_applications"],
    label: "Primary Recruiter",
    field: "Primary Recruiter",
    group: "Recruitment",
    type: "string",
  },
  QUESTION_LABEL: {
    datasetName: ["culture"],
    label: "Question Label",
    field: "question_label",
    group: "Organization",
    type: "string",
  },
  QUESTION_THEME: {
    datasetName: ["culture"],
    label: "Question Theme",
    field: "question_theme",
    group: "Organization",
    type: "string",
  },
  QUESTION_SUBTHEME: {
    datasetName: ["culture"],
    label: "Question Subtheme",
    field: "question_subtheme",
    group: "Organization",
    type: "string",
  },
  PRIMARY_WORK_LOCATION: {
    datasetName: ["culture"],
    label: "Primary Work Location",
    field: "Primary Work Location",
    group: "Organization",
    type: "string",
  },
  RACE: {
    datasetName: ["benchmark"],
    label: "Race",
    field: "Race",
    group: "Benchmark",
    type: "string",
  },
  RACE_GROUPING: {
    datasetName: ["benchmark"],
    label: "Race Grouping",
    field: "RaceGrouping",
    group: "Benchmark",
    type: "string",
  },
  RECRUITMENT_STAGE: {
    datasetName: ["recruitment_applications"],
    label: "Current Recruitment Stage",
    field: "Current_Stage",
    group: "Recruitment",
    type: "string",
  },
  RECRUITMENT_STAGE_HISTORICAL: {
    datasetName: ["recruitment_applications"],
    label: "Recruitment Stage (Historical)",
    field: "RECRUITMENT_STAGE_HISTORICAL",
    group: "Recruitment",
    type: "special:string",
  },
  REGION: {
    datasetName: ["main", "culture"],
    label: "Region",
    field: "region",
    group: "Geography",
    type: "string",
  },
  REGRETTABLE_ATTRITION: {
    datasetName: ["main", "employee_goals"],
    label: "Regrettable Attrition",
    field: "regrettable_nonRegrettable",
    group: "Organization",
    type: "string",
  },
  SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT: {
    datasetName: ["lightcast"],
    label: "Sector Share of Area Unemployment",
    field: "sector_unemployment_share_of_area_unemployment",
    group: "Industry",
    type: "number",
  },
  SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE: {
    datasetName: ["lightcast"],
    label: "Sector Unemployed Workers (Trailing 12M)",
    field: "sector_unemployment_trailing_12m_unemployed_workers_average",
    group: "Industry",
    type: "number",
  },
  SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY: {
    datasetName: ["lightcast"],
    label: "Sector Unemployment Change (YoY)",
    field: "sector_unemployment_unemployed_change_yoy",
    group: "Industry",
    type: "number",
  },
  SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS: {
    datasetName: ["lightcast"],
    label: "Sector Unemployed Workers",
    field: "sector_unemployment_unemployed_workers",
    group: "Industry",
    type: "number",
  },
  SOC_TITLE: {
    datasetName: ["main"],
    label: "SOC Title",
    field: "SOC_Title",
    group: "Industry",
    type: "string",
  },
  STATE: {
    datasetName: ["main", "benchmark"],
    label: "State",
    field: "state",
    group: "Geography",
    type: "string",
  },
  STORE_NAME: {
    datasetName: ["main"],
    label: "Store Name",
    field: "store_name",
    group: "Organization",
    type: "string",
  },
  STORE_STATUS: {
    datasetName: ["main"],
    label: "Store Status",
    field: "storeStatus",
    group: "Organization",
    type: "string",
  },
  SUBMIT_DATE: {
    datasetName: ["culture"],
    label: "Submit Date",
    field: "submit_date",
    group: "Organization",
    type: "date",
  },
  SUPERVISOR_ID: {
    datasetName: ["main"],
    label: "Supervisor ID",
    field: "supervisorId",
    group: "Organization",
    type: "string",
  },
  SUPERVISOR_JOB_TITLE: {
    datasetName: ["main"],
    label: "Supervisor Job Title",
    field: "supervisorJobTitle",
    group: "Organization",
    type: "string",
  },
  SUPERVISOR_NAME: {
    datasetName: ["employee_relations"],
    label: "Supervisor Name",
    field: "Supervisor Name Preferred (Unsecured)",
    group: "Employee Relations",
    type: "string",
  },
  SURVEY_NAME: {
    datasetName: ["culture"],
    label: "Survey Name",
    field: "survey_name",
    group: "Organization",
    type: "string",
  },
  SURVEY_STATUS: {
    datasetName: ["culture"],
    label: "Survey Status",
    field: "status",
    group: "Organization",
    type: "string",
  },
  TENURE_GROUP: {
    datasetName: ["main"],
    label: "Tenure Group",
    field: "tenure_group",
    group: "Organization",
    type: "string",
  },
  TERMINATION_DATE: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Termination Date",
    field: "terminationDate",
    group: "Organization",
    type: "date",
  },
  TERMINATION_REASON: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Termination Reason",
    field: "terminationReason",
    group: "Organization",
    type: "string",
  },
  TERMINATION_TYPE: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Termination Type",
    field: "voluntary_involuntary",
    group: "Organization",
    type: "string",
  },
  TIME_TO_FILL_DAYS: {
    datasetName: ["main", "recruitment_applications"],
    label: "Time to Fill (Days)",
    field: "Time to Fill (Days)",
    group: "Recruitment",
    type: "number",
  },
  TIME_TO_FILL_DRIVERS_IMPACT: {
    datasetName: ["time_to_fill_drivers"],
    label: "Time to Fill Drivers",
    field: "impact",
    group: "Recruitment",
    type: "number",
  },
  TIME_TO_HIRE_DAYS: {
    datasetName: ["recruitment_applications"],
    label: "Time to Hire (Days)",
    field: "TIME_TO_HIRE_DAYS",
    group: "Recruitment",
    type: "special:number",
  },
  TOTAL_COMPENSATION: {
    datasetName: ["main", "culture", "employee_goals"],
    label: "Total Compensation",
    field: "total_compensation",
    group: "Organization",
    type: "number",
  },
  VETERAN_STATUS: {
    datasetName: ["main", "recruitment_applications"],
    label: "Veteran Status",
    field: "veteran_status",
    group: "Demographics",
    type: "string",
  },
  WORK_ARRANGEMENT_TYPE: {
    datasetName: ["main"],
    label: "Work Arrangement Type",
    field: "workArrangementType",
    group: "Organization",
    type: "string",
  },
} as const satisfies Record<FieldName, FieldBase>;

export type ColumnName =
  (typeof FIELD_BASES)[keyof typeof FIELD_BASES]["field"];
